import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { todayLocalIso } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useLivePoll } from "@/hooks/useLivePoll";
import {
  Loader2, RefreshCw, Plus, Search, AlertTriangle, Check, FileDown, Mail, ChevronRight,
  ClipboardList, X as XIcon, Sun, Moon, Calendar as CalendarIcon, ShieldAlert,
  Zap, Flame, ArrowUp, HardHat, FlaskConical, Droplets, Wrench, Save,
} from "lucide-react";
import EmailModal from "@/components/EmailModal";
import PhotoUpload from "@/components/PhotoUpload";
import { downloadReportPdf } from "@/lib/reportPdf";

/**
 * Handovers — list + rich "New Handover" modal that mirrors the mobile app.
 *
 * Field sequence (exactly matches native app):
 *   1. Asset Number (dropdown from /assets)
 *   2. SMU (Hours) — numeric
 *   3. Current Shift (Day / Night button toggle)
 *   4. Date (date picker)
 *   5. Tradesman (auto-filled from profile, editable)
 *   6. Work Completed
 *   7. Machine Status
 *   8. Operator Complaint
 *   9. Delays / Parts
 *  10. Next Priorities
 *  11. HAZARDS — pill chooser + "OR TYPE A CUSTOM HAZARD" input
 *  12. Photos
 */
const HAZARD_CATALOG = [
  { id: "stored-energy", label: "Stored Energy", icon: Zap },
  { id: "hot-work",      label: "Hot Work",      icon: Flame },
  { id: "height",        label: "Working at Height", icon: ArrowUp },
  { id: "confined",      label: "Confined Space", icon: HardHat },
  { id: "chemicals",     label: "Chemicals",     icon: FlaskConical },
  { id: "wet-slippery",  label: "Wet / Slippery", icon: Droplets },
  { id: "moving-parts",  label: "Moving Parts",  icon: Wrench },
];

export default function Handovers() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [emailFor, setEmailFor] = useState(null);

  const reload = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get("/handovers");
      const arr = Array.isArray(data) ? data : data?.items || [];
      setItems(arr);
    } catch (ex) { setErr(apiError(ex)); setItems([]); }
    finally { setBusy(false); }
  };
  useEffect(() => { reload(); }, []);
  useLivePoll(reload, 5000, true);

  const filtered = useMemo(() => {
    let list = items || [];
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((it) => {
        const hay = `${it.asset_id || ""} ${it.tradesman || ""} ${it.shift || ""} ${it.breakdown_fault || ""} ${it.work_completed || ""} ${it.safety_issues || ""}`.toLowerCase();
        return hay.includes(s);
      });
    }
    return list.sort((a, b) => new Date(b.created_at || b.date || 0) - new Date(a.created_at || a.date || 0));
  }, [items, q]);

  return (
    <div className="space-y-5" data-testid="handovers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">Pit Link</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-none mt-1">Operational Shift Handover</h1>
          <div className="text-muted mt-2 text-sm font-mono-tight">
            /handovers · One form per shift · Auto-filled from your profile
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm btn-ghost" onClick={reload} disabled={busy} data-testid="handovers-refresh">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
          <button className="btn btn-sm btn-primary" onClick={() => setShowNew(true)} data-testid="handovers-new">
            <Plus className="w-4 h-4" /> New Handover
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="panel-flat px-3 flex items-center gap-2">
        <Search className="w-4 h-4 text-muted shrink-0" />
        <input
          className="w-full py-3 bg-transparent outline-none text-base"
          placeholder="Search by asset ID, tradesman, shift, notes…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="handovers-search"
        />
      </div>

      {err && <div className="pill pill-red !text-sm !py-2 !px-3 justify-start"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>}

      {items === null ? (
        <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading handovers…
        </div>
      ) : filtered.length === 0 ? (
        <div className="panel-flat p-10 text-center text-muted">
          <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-60" /> No handovers logged.
        </div>
      ) : (
        <div className="grid gap-2" data-testid="handovers-list">
          {filtered.map((h) => (
            <div
              key={h.id || h._id}
              className="panel p-4 flex items-center gap-4 cursor-pointer group hover:border-orange-500/40"
              onClick={() => nav(`/handovers/${h.id || h._id}`)}
              data-testid={`handover-card-${h.id || h._id}`}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: shiftBg(h.shift), border: `1px solid ${shiftBorder(h.shift)}`, color: shiftColor(h.shift) }}
              >
                {String(h.shift || "").toLowerCase().includes("night") ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="pill !text-[10px] !py-0.5 !px-2 font-mono-tight">{h.asset_id || "Asset"}</span>
                  <span className="pill !text-[10px] !py-0.5 !px-2 uppercase font-black tracking-widest">{h.shift || "shift"}</span>
                  {h.hazards && h.hazards.length > 0 && (
                    <span className="pill !text-[10px] !py-0.5 !px-2" style={{ background: "#5a0f0f", border: "1px solid #a51919", color: "#ff9b9b" }}>
                      <ShieldAlert className="w-3 h-3" /> {h.hazards.length} hazard{h.hazards.length === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
                <div className="font-display font-black text-base mt-1.5 truncate">
                  {h.breakdown_fault || h.work_completed || h.machine_status || "Shift handover"}
                </div>
                {h.breakdown_fault && h.work_completed && (
                  <div className="text-[11px] text-white/70 font-mono-tight mt-0.5 truncate">
                    Fixed: {h.work_completed}
                  </div>
                )}
                <div className="text-[11px] text-muted font-mono-tight mt-0.5 truncate">
                  {h.tradesman || "—"} · {fmt(h.date || h.created_at)}
                  {h.smu && <> · SMU {h.smu}</>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                <button className="btn btn-sm btn-ghost" onClick={() => downloadReportPdf(h, "Handover")} title="Download PDF" data-testid={`handover-pdf-${h.id || h._id}`}>
                  <FileDown className="w-4 h-4" />
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => setEmailFor(h)} title="Email" data-testid={`handover-email-${h.id || h._id}`}>
                  <Mail className="w-4 h-4" />
                </button>
                <ChevronRight className="w-4 h-4 text-muted group-hover:text-white" />
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <NewHandoverModal
          user={user}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); reload(); }}
        />
      )}
      {emailFor && (
        <EmailModal open={true} kind="Handover" item={emailFor} onClose={() => setEmailFor(null)} />
      )}
    </div>
  );
}

/* ---------------- New Handover modal — matches mobile app field sequence ---------------- */
function NewHandoverModal({ user, onClose, onCreated }) {
  const [assets, setAssets] = useState([]);
  const [assetId, setAssetId] = useState("");
  const [smu, setSmu] = useState("");
  const [shift, setShift] = useState("day");
  const [date, setDate] = useState(todayLocalIso());
  const [tradesman, setTradesman] = useState(user?.name || user?.full_name || user?.username || "");
  const [breakdownFault, setBreakdownFault] = useState("");
  const [workCompleted, setWorkCompleted] = useState("");
  const [machineStatus, setMachineStatus] = useState("");
  const [operatorComplaint, setOperatorComplaint] = useState("");
  const [delaysParts, setDelaysParts] = useState("");
  const [nextPriorities, setNextPriorities] = useState("");
  const [safetyIssues, setSafetyIssues] = useState("");
  const [hazards, setHazards] = useState([]);
  const [customHazard, setCustomHazard] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/assets");
        const arr = Array.isArray(data) ? data : data?.items || [];
        setAssets(arr.filter((a) => !a.retired));
      } catch { /* noop */ }
    })();
  }, []);

  const toggleHazard = (id) => {
    setHazards((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const addCustomHazard = () => {
    const v = customHazard.trim();
    if (!v) return;
    setHazards((prev) => [...prev, v]);
    setCustomHazard("");
  };

  const submit = async () => {
    setMsg(null);
    if (!assetId) { setMsg({ type: "err", text: "Pick an asset." }); return; }
    if (!tradesman.trim()) { setMsg({ type: "err", text: "Tradesman name required." }); return; }

    setBusy(true);
    try {
      const body = {
        asset_id: assetId,
        smu: smu || "",
        shift,
        date,
        tradesman: tradesman.trim(),
        breakdown_fault: breakdownFault,
        work_completed: workCompleted,
        machine_status: machineStatus,
        operator_complaint: operatorComplaint,
        delays_parts: delaysParts,
        next_priorities: nextPriorities,
        safety_issues: safetyIssues,
        photos: photos.map((p) => p.data || p),
        hazards: hazards.map((h) => {
          const cat = HAZARD_CATALOG.find((x) => x.id === h);
          return cat ? { id: h, label: cat.label, added_at: new Date().toISOString(), added_by: user?.name || user?.username } : { id: h, label: h, added_at: new Date().toISOString(), added_by: user?.name || user?.username };
        }),
        sign_off_name: user?.name || user?.username || tradesman,
        client_op_id: `handover-${Date.now()}`,
      };
      await api.post("/handovers", body);
      setMsg({ type: "ok", text: "Handover filed." });
      setTimeout(onCreated, 500);
    } catch (ex) {
      setBusy(false);
      setMsg({ type: "err", text: apiError(ex) });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="handover-modal">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }} onClick={onClose} />
      <div className="relative panel-heavy w-full max-w-3xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 p-5" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
            <ClipboardList className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted">New Handover</div>
            <div className="font-display font-black text-xl tracking-tight">End of shift · quick log</div>
          </div>
          <button className="btn btn-sm btn-ghost !px-2" onClick={onClose} data-testid="handover-close">
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {/* 1. Asset dropdown */}
          <div className="field">
            <label>Asset Number *</label>
            <select className="input" value={assetId} onChange={(e) => setAssetId(e.target.value)} data-testid="handover-asset">
              <option value="">— Pick asset —</option>
              {assets.map((a) => (
                <option key={a.id || a._id} value={a.id || a._id}>
                  {a.id || a._id}{a.name ? ` · ${a.name}` : ""}{a.machine_model ? ` (${a.machine_model})` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* 2. SMU */}
          <div className="field">
            <label>SMU (Hours)</label>
            <input
              className="input font-mono-tight text-lg"
              type="number"
              inputMode="numeric"
              value={smu}
              onChange={(e) => setSmu(e.target.value)}
              placeholder="e.g. 12345"
              data-testid="handover-smu"
            />
          </div>

          {/* 3. Current Shift — Day / Night toggle */}
          <div className="field">
            <label>Current Shift *</label>
            <div className="grid grid-cols-2 gap-2" data-testid="handover-shift">
              <ShiftBtn on={shift === "day"} onClick={() => setShift("day")} label="Day" icon={Sun} testid="handover-shift-day" />
              <ShiftBtn on={shift === "night"} onClick={() => setShift("night")} label="Night" icon={Moon} testid="handover-shift-night" />
            </div>
          </div>

          {/* 4. Date */}
          <div className="field">
            <label className="flex items-center gap-2"><CalendarIcon className="w-3.5 h-3.5" /> Date *</label>
            <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="handover-date" />
          </div>

          {/* 5. Tradesman — pre-filled */}
          <div className="field">
            <label className="flex items-center gap-2">
              Tradesman *
              <span className="pill !text-[9px] !py-0 !px-1.5 uppercase tracking-widest" style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
                Profile
              </span>
            </label>
            <input
              className="input"
              value={tradesman}
              onChange={(e) => setTradesman(e.target.value)}
              placeholder="e.g. J. Smith"
              data-testid="handover-tradesman"
            />
          </div>

          {/* 6-10: shift notes — "Breakdown Fault" captures the state of the
              machine BEFORE the shift's work started, so the incoming crew
              can see what problem was being chased. Added Feb 9 per Josh's
              request. Sits above "Work Completed" so the story reads
              naturally: fault → what we did → what's next. */}
          <TextField label="Breakdown Fault" value={breakdownFault} onChange={setBreakdownFault} testid="handover-breakdown-fault" />
          <TextField label="Work Completed" value={workCompleted} onChange={setWorkCompleted} testid="handover-work-completed" />
          <TextField label="Machine Status" value={machineStatus} onChange={setMachineStatus} testid="handover-machine-status" />
          <TextField label="Operator Complaint" value={operatorComplaint} onChange={setOperatorComplaint} testid="handover-operator-complaint" />
          <TextField label="Delays / Parts" value={delaysParts} onChange={setDelaysParts} testid="handover-delays" />
          <TextField label="Next Priorities" value={nextPriorities} onChange={setNextPriorities} testid="handover-priorities" />

          {/* 11: HAZARDS section — pill chooser */}
          <div className="panel p-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="w-4 h-4" style={{ color: "var(--primary)" }} />
              <div className="text-[10px] uppercase tracking-[0.3em] font-black text-muted">Site Hazards</div>
              {hazards.length > 0 && (
                <span className="pill pill-orange !text-[10px] !py-0.5 !px-2" data-testid="handover-hazard-count">
                  {hazards.length} logged
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="handover-hazards-pills">
              {HAZARD_CATALOG.map((h) => {
                const on = hazards.includes(h.id);
                const Icon = h.icon;
                return (
                  <button
                    key={h.id}
                    onClick={() => toggleHazard(h.id)}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-colors ${
                      on ? "text-black" : "text-white/80 hover:text-white"
                    }`}
                    style={on
                      ? { background: "var(--primary)" }
                      : { background: "var(--surface-2)", border: "1px solid var(--border)" }}
                    data-testid={`hazard-pill-${h.id}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {h.label}
                  </button>
                );
              })}
            </div>

            {/* Custom hazards already added */}
            {hazards.filter((h) => !HAZARD_CATALOG.some((c) => c.id === h)).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {hazards.filter((h) => !HAZARD_CATALOG.some((c) => c.id === h)).map((h) => (
                  <span key={h} className="pill pill-orange-solid !text-[10px] !py-1 !px-2.5" data-testid={`hazard-custom-${h}`}>
                    {h}
                    <button className="ml-1 opacity-70 hover:opacity-100" onClick={() => toggleHazard(h)} title="Remove">
                      <XIcon className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Custom hazard input */}
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-[0.3em] font-black text-muted mb-1.5">Or type a custom hazard</div>
              <div className="flex gap-2">
                <input
                  className="input flex-1"
                  value={customHazard}
                  onChange={(e) => setCustomHazard(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomHazard(); } }}
                  placeholder="e.g. Oil spill near bay 3"
                  data-testid="handover-custom-hazard-input"
                />
                <button className="btn" onClick={addCustomHazard} disabled={!customHazard.trim()} data-testid="handover-custom-hazard-add">
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>
            </div>
          </div>

          <TextField label="Safety Issues" value={safetyIssues} onChange={setSafetyIssues} testid="handover-safety-issues" />

          {/* Photos */}
          <div className="field">
            <label>Photos</label>
            <PhotoUpload value={photos} onChange={setPhotos} max={6} accept="image/*" label="Photos" />
          </div>

          {msg && (
            <div className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`} data-testid="handover-msg">
              {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {msg.text}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy} data-testid="handover-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            File Handover
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- bits ---------------- */
function ShiftBtn({ on, onClick, label, icon: Icon, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-14 rounded-xl flex items-center justify-center gap-2 font-display font-black uppercase tracking-widest transition-colors ${
        on ? "text-black" : "text-white/80 hover:text-white"
      }`}
      style={on ? { background: "var(--primary)" } : { background: "var(--surface-2)", border: "1px solid var(--border)" }}
      data-testid={testid}
    >
      <Icon className="w-5 h-5" /> {label}
    </button>
  );
}
function TextField({ label, value, onChange, testid, rows = 2 }) {
  return (
    <div className="field">
      <label>{label}</label>
      <textarea className="input" rows={rows} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
function shiftBg(s) { return String(s || "").toLowerCase().includes("night") ? "#141a2f" : "rgba(245,130,32,0.15)"; }
function shiftBorder(s) { return String(s || "").toLowerCase().includes("night") ? "#3b60d1" : "rgba(245,130,32,0.3)"; }
function shiftColor(s) { return String(s || "").toLowerCase().includes("night") ? "#8bb4ff" : "var(--primary)"; }
function fmt(v) { if (!v) return "—"; try { return new Date(v).toLocaleString(); } catch { return String(v); } }
